import ContentPage from '@/components/feature/ContentPage';

export default function ReviewPage() {
  return (
    <ContentPage
      title="CSSBuy Review: What You Need to Know"
      metaTitle="CSSBuy Review 2026: Features, Pros, Cons and Safety | CSSBuyBR"
      metaDescription="Complete CSSBuy review covering features, warehouse, QC, shipping, payment, safety and alternatives. Independent resource for informed decisions."
      subtitle="Before using any international shopping service, understand the platform, its processes, and potential risks."
      showToc
      sections={[
        { id: 'overview', title: 'Overview' },
        { id: 'features', title: 'Features' },
        { id: 'pros', title: 'Pros' },
        { id: 'cons', title: 'Cons' },
        { id: 'warehouse', title: 'Warehouse' },
        { id: 'qc', title: 'QC & Inspection' },
        { id: 'shipping', title: 'Shipping' },
        { id: 'payment', title: 'Payment' },
        { id: 'safety', title: 'Safety' },
        { id: 'alternatives', title: 'Alternatives' },
      ]}
      content={
        <div className="space-y-6">
          <section id="overview">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Overview</h2>
            <p>CSSBuy is a shopping agent platform that facilitates international purchases from Chinese marketplaces. It acts as an intermediary, handling ordering, inspection, consolidation, and shipping to buyers worldwide. Understanding how the platform works is essential before committing to any transaction.</p>
          </section>

          <section id="features">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Key Features</h2>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Product sourcing from multiple Chinese platforms</li>
              <li>Quality control and inspection services</li>
              <li>Package consolidation to reduce shipping costs</li>
              <li>Multiple international shipping options</li>
              <li>Real-time order tracking</li>
              <li>Customer support for order issues</li>
            </ul>
          </section>

          <section id="pros">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Advantages</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Wide product selection across categories',
                'QC photo inspection before shipping',
                'Package consolidation saves on shipping',
                'Multiple shipping methods available',
                'Community resources and guides available',
                'Competitive service fees in many cases',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="cons">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Considerations</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'International shipping can be complex',
                'Potential customs duties and taxes',
                'Language barriers for some users',
                'Return process may be complicated',
                'Shipping times can be lengthy',
                'Exchange rate fluctuations affect costs',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-alert-line text-secondary-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="warehouse">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Warehouse Services</h2>
            <p>CSSBuy provides warehouse storage where your items are held before shipping. Key warehouse features include:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Free storage period (typically 30-90 days depending on plan)</li>
              <li>Item inspection upon arrival at warehouse</li>
              <li>Package consolidation and repackaging</li>
              <li>Photo documentation of received items</li>
              <li>Customs declaration assistance</li>
            </ul>
          </section>

          <section id="qc">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">QC & Inspection</h2>
            <p>Quality Control is a critical service. When items arrive at the warehouse, CSSBuy can provide detailed photos showing:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Overall condition and appearance</li>
              <li>Branding and logo accuracy</li>
              <li>Material quality and texture</li>
              <li>Size and fit verification</li>
              <li>Packaging condition</li>
            </ul>
            <p className="mt-2">Always review QC photos carefully before approving shipment. If issues are found, you can request returns or exchanges before international shipping begins.</p>
          </section>

          <section id="shipping">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Shipping Options</h2>
            <p>CSSBuy offers multiple shipping carriers and methods. Common options include:</p>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Method</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Speed</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Best For</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    ['Express', 'Fastest', 'Urgent or high-value items'],
                    ['Standard', 'Moderate', 'Regular orders with no rush'],
                    ['Economy', 'Slower', 'Budget-conscious buyers'],
                    ['Sea Freight', 'Slowest', 'Very heavy or bulky items'],
                  ].map(([m, s, b], i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium text-foreground-800">{m}</td>
                      <td className="px-3 py-2 text-foreground-600">{s}</td>
                      <td className="px-3 py-2 text-foreground-600">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="payment">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Payment Methods</h2>
            <p>CSSBuy typically supports various payment options. Common methods include PayPal, credit cards, and platform-specific payment systems. Always verify current accepted payment methods directly on the official website before placing orders.</p>
          </section>

          <section id="safety">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Safety & Trust</h2>
            <p>CSSBuy is a known platform in the international shopping community, but like any online service, exercising caution is important:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Always verify you are on the official website</li>
              <li>Never share login credentials with third parties</li>
              <li>Review community feedback before large purchases</li>
              <li>Understand the dispute and refund process</li>
              <li>Keep all transaction records and communications</li>
            </ul>
          </section>

          <section id="alternatives">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Alternatives</h2>
            <p>Several other shopping agent platforms exist in the market. Research and compare service fees, shipping rates, customer service quality, and community reputation before choosing the platform that best fits your needs.</p>
          </section>

          <div className="mt-10 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Continue Your Research</h3>
            <p className="text-sm text-primary-700 mb-3">Browse the full CSSBuy product catalog and explore current offerings on the main site.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}